import type { MarketRegime, MarketAnalyticsSnapshot, RiskLevel, RndMoments, Tier1GexMetrics, VolFlowMetrics } from '../types/marketAnalytics';
import type { OptionRow } from '../services/gexCalculator';
import { computeBreedenLitzenberger } from './breedenLitzenberger';
import { buildGexMetrics, buildVolFlowMetrics, buildDteBuckets, gexLevelsFromRows } from './gexAnalyticsMetrics';
import { buildGexStrikeBand } from './gexWallHeatmap';
import { buildVolAnalytics } from './volAnalyticsMetrics';
import { buildPinAnalytics } from './pinAnalyticsMetrics';
import { buildTier2Metrics } from './tier2AnalyticsMetrics';
import { buildSpreadAnalytics } from './spreadAnalyticsMetrics';

/** Sovereign v2 trained thresholds (subset). */
const TH = {
  gex_strong: 50000,
  wall_spread_calm_max: 18.32,
  wall_spread_extreme_min: 30,
  skew_bearish: -0.8,
  kurt_extreme: 8,
  rvol_sleepy: 0.12,
};

export function classifyRegime(
  gex: Tier1GexMetrics,
  vol: VolFlowMetrics,
  rnd: RndMoments | null,
  rows: OptionRow[],
): { regime: MarketRegime; confidence: number; risk_level: RiskLevel } {
  let calm = 0;
  let momentum = 0;
  let extreme = 0;
  let sleepy = 0;

  if (gex.dollar_gex > TH.gex_strong) calm += 2;
  if (gex.gex_zscore != null && gex.gex_zscore > 0.5) calm += 1;
  if (gex.trap_zone) extreme += 3;
  if (gex.gex_zscore != null && gex.gex_zscore < -1) extreme += 2;
  if (rnd && rnd.kurtosis >= TH.kurt_extreme) extreme += 2;
  if (rnd && rnd.skewness <= TH.skew_bearish) extreme += 1;
  if (vol.z_vov != null && vol.z_vov > 1.5) extreme += 1;

  const totalVol = rows.reduce((s, r) => s + (Number(r.Volume ?? r.volume ?? 0) || 0), 0);
  const rvolProxy = totalVol / Math.max(rows.length, 1) / 1000;
  if (rvolProxy < TH.rvol_sleepy) sleepy += 2;
  if (gex.minutes_to_close > 180 && rvolProxy < 0.2) sleepy += 1;

  if (vol.signed_buyer_pcr != null && (vol.signed_buyer_pcr > 1.2 || vol.signed_buyer_pcr < 0.7)) {
    momentum += 2;
  }
  if (gex.gamma_flip_distance != null && gex.gamma_flip_distance > 0.04) momentum += 1;
  if (gex.dollar_gex < 0) momentum += 1;

  const scores: Record<MarketRegime, number> = {
    CALM: calm,
    MOMENTUM: momentum,
    EXTREME: extreme,
    SLEEPY: sleepy,
  };
  const sorted = (Object.entries(scores) as [MarketRegime, number][]).sort((a, b) => b[1] - a[1]);
  const regime = sorted[0][1] > 0 ? sorted[0][0] : 'CALM';
  const top = sorted[0][1];
  const second = sorted[1]?.[1] ?? 0;
  const confidence = top > 0 ? Math.min(0.95, 0.45 + (top - second) * 0.12) : 0.4;

  let risk_level: RiskLevel = 'LOW';
  if (regime === 'EXTREME' || gex.trap_zone) risk_level = 'HIGH';
  else if (regime === 'MOMENTUM' || (gex.gex_zscore != null && gex.gex_zscore < 0)) risk_level = 'MEDIUM';

  return { regime, confidence, risk_level };
}

export function buildMarketAnalyticsSnapshot(
  symbol: string,
  rows: OptionRow[],
  regimePrev?: MarketRegime | null,
): MarketAnalyticsSnapshot {
  const levels = gexLevelsFromRows(rows);
  const gex = buildGexMetrics(symbol, levels);
  const vol_flow = buildVolFlowMetrics(rows);
  const vol_analytics = buildVolAnalytics(rows, gex.spot, gex.minutes_to_close);
  if (vol_analytics.iv_har_forecast != null && vol_analytics.atm_iv != null) {
    vol_flow.iv_har_gap = vol_analytics.atm_iv - vol_analytics.iv_har_forecast;
  }
  const dte_buckets = buildDteBuckets(rows);
  const gex_strike_band = buildGexStrikeBand(levels);
  const pin_analytics = buildPinAnalytics(levels, rows, gex.spot, gex.minutes_to_close);
  const tier2 = buildTier2Metrics(gex, vol_flow, vol_analytics, dte_buckets, rows);
  const spread_analytics = buildSpreadAnalytics(rows, gex.spot, vol_analytics.atm_iv);
  const { curve, moments } = computeBreedenLitzenberger(rows, gex.spot);
  const { regime, confidence, risk_level } = classifyRegime(gex, vol_flow, moments, rows);
  const transitioned = Boolean(regimePrev && regimePrev !== regime);

  return {
    symbol,
    as_of: new Date().toISOString(),
    regime,
    regime_confidence: confidence,
    risk_level,
    regime_prev: regimePrev ?? null,
    transitioned,
    gex,
    vol_flow,
    rnd: moments ?? {
      forward: gex.spot,
      forward_pct: 0,
      implied_vol: null,
      skewness: 0,
      kurtosis: 3,
      tail_ratio: 1,
      regime_skew: 'neutral',
    },
    rnd_curve: curve,
    dte_buckets,
    gex_strike_band,
    vol_analytics,
    pin_analytics,
    tier2,
    spread_analytics,
    factors: {
      dollar_gex: gex.dollar_gex,
      gex_zscore: gex.gex_zscore,
      trap_zone: gex.trap_zone,
      pin_probability: gex.pin_probability,
      z_vov: vol_flow.z_vov,
      iv_har_gap: vol_flow.iv_har_gap,
      signed_buyer_pcr: vol_flow.signed_buyer_pcr,
      vpin_rolling: vol_flow.vpin_rolling,
      flow_toxicity: vol_flow.flow_toxicity,
      minutes_to_close: gex.minutes_to_close,
      gamma_acceleration: gex.gamma_acceleration,
      rnd_skewness: moments?.skewness ?? null,
      dealer_constraint: tier2.dealer_constraint,
      short_vrp: vol_analytics.short_vrp,
      surface_curvature: vol_analytics.surface_curvature,
      intraday_vol_regime: vol_analytics.intraday_regime,
      pin_gravity_strike: pin_analytics.gravity_strike,
      opening_drive: tier2.opening_drive,
      cwr_proxy: tier2.cwr_proxy,
      optimal_wing_pct: spread_analytics.optimal_wing_pct,
      optimal_cwr: spread_analytics.optimal_cwr,
    },
  };
}
